import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, LinearProgress, MenuItem, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add, Delete, Edit, LocalShipping } from '@mui/icons-material';
import api from '../../services/api';
import type { CustomerOrder, CustomerOrderStatus, CustomerPlant, SaveCustomerOrder } from '../../types/factory';
import CustomerPicker, { PickedCustomer } from '../../components/commercial/CustomerPicker';
import { formatDay } from '../../components/commercial/contact';
import { PageHeader, ProductSelect, errorMessage, fmtMoney, fmtMoney2, fmtPct, fmtQty, isoDay, num, todayIso, usePageView } from './shared';
import type { PageView } from './shared';

const STATUS: Record<CustomerOrderStatus, { label: string; color: 'primary' | 'success' | 'default' }> = {
  Open: { label: 'Abierta', color: 'primary' },
  Closed: { label: 'Cumplida', color: 'success' },
  Cancelled: { label: 'Anulada', color: 'default' },
};

const VIEWS: PageView[] = [
  { key: 'ordenes', label: 'Pedidos de clientes', description: 'OC abiertas a precio fijo que se van entregando por remito a cada planta. Cada entrega puede generar la venta en la cuenta corriente.' },
  { key: 'plantas', label: 'Plantas y expresos de clientes', description: 'Adónde se entrega a cada cliente y con qué transporte.' },
];

/** Órdenes de compra abiertas de clientes, entregas por remito a cada planta y saldo por entregar. */
const FactoryCustomerOrdersPage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Órdenes de compra de clientes'}
        description={flat && current.description ? current.description : <>OC abiertas a precio fijo que se van entregando por remito a cada planta. Cada entrega puede generar la venta en la cuenta corriente del cliente.</>}
      />
      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Órdenes de compra" />
        <Tab label="Plantas y expresos" />
      </Tabs>)}
      {tab === 0 ? <OrdersTab /> : <PlantsTab />}
    </Box>
  );
};

// ==================== Órdenes ====================

type OrderForm = Omit<SaveCustomerOrder, 'quantity' | 'unitPrice' | 'customerId'> & {
  id?: number;
  customer: PickedCustomer | null;
  quantity: string;
  unitPrice: string;
};

const OrdersTab: React.FC = () => {
  const [status, setStatus] = useState<'Open' | 'Closed' | ''>('Open');
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [rows, setRows] = useState<CustomerOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<OrderForm | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.getCustomerOrders({ status: status || undefined, customerId: customer?.id }));
    } catch (e) {
      setError(errorMessage(e, 'No se pudieron cargar las órdenes'));
    }
  }, [status, customer]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form) return;
    if (!form.customer) { setError('Elegí el cliente'); return; }
    const payload: SaveCustomerOrder = {
      customerId: form.customer.id, number: form.number, orderDate: form.orderDate, productId: form.productId,
      quantity: num(form.quantity), unitPrice: num(form.unitPrice), status: form.status, notes: form.notes || null,
    };
    try {
      if (form.id) await api.updateCustomerOrder(form.id, payload);
      else await api.createCustomerOrder(payload);
      setForm(null);
      setError(null);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar la orden'));
    }
  };

  const openBalance = useMemo(() => (rows || []).filter(r => r.status === 'Open').reduce((a, r) => a + r.balance * r.unitPrice, 0), [rows]);

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={status} onChange={(_, v) => v !== null && setStatus(v)}>
          <ToggleButton value="Open">Abiertas</ToggleButton>
          <ToggleButton value="Closed">Cumplidas</ToggleButton>
          <ToggleButton value="">Todas</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ minWidth: 260 }}><CustomerPicker value={customer} onChange={setCustomer} /></Box>
        <Box sx={{ flex: 1 }} />
        {status === 'Open' && rows && rows.length > 0 && (
          <Typography variant="body2" color="text.secondary">Saldo por entregar: <b>{fmtMoney(openBalance)}</b></Typography>
        )}
        <Button variant="contained" startIcon={<Add />} onClick={() => setForm({
          customer: customer, number: '', orderDate: todayIso(), productId: 0, quantity: '', unitPrice: '', status: 'Open', notes: '',
        })}>Nueva OC</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!rows ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>OC</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Entregado</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell>Última entrega</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay órdenes de compra.</TableCell></TableRow>
                )}
                {rows.map(r => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(r.id)}>
                    <TableCell>{r.customerName}</TableCell>
                    <TableCell>{r.number}</TableCell>
                    <TableCell>{formatDay(r.orderDate)}</TableCell>
                    <TableCell>{r.productName}</TableCell>
                    <TableCell align="right">{fmtQty(r.quantity)}</TableCell>
                    <TableCell align="right">{fmtMoney2(r.unitPrice)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress variant="determinate" value={Math.min(100, r.deliveredPct * 100)} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                        <Typography variant="caption">{fmtPct(r.deliveredPct)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmtQty(r.balance)}</TableCell>
                    <TableCell>{formatDay(r.lastDeliveryDate)}</TableCell>
                    <TableCell><Chip size="small" color={STATUS[r.status].color} label={STATUS[r.status].label} /></TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => setForm({
                        id: r.id, customer: { id: r.customerId, name: r.customerName }, number: r.number, orderDate: isoDay(r.orderDate),
                        productId: r.productId, quantity: String(r.quantity), unitPrice: String(r.unitPrice), status: r.status, notes: r.notes || '',
                      })}><Edit fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Editar orden de compra' : 'Nueva orden de compra'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <CustomerPicker value={form.customer} onChange={c => setForm({ ...form, customer: c })} />
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="N° de OC" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} required />
                <TextField size="small" type="date" label="Fecha" value={form.orderDate} onChange={e => setForm({ ...form, orderDate: e.target.value })} InputLabelProps={{ shrink: true }} />
              </Stack>
              <ProductSelect value={form.productId || null} onChange={id => setForm({ ...form, productId: id ?? 0 })} required />
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label="Cantidad" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} required />
                <TextField size="small" type="number" label="Precio unitario $" value={form.unitPrice} onChange={e => setForm({ ...form, unitPrice: e.target.value })}
                  helperText={num(form.quantity) && num(form.unitPrice) ? `Total ${fmtMoney(num(form.quantity) * num(form.unitPrice))}` : ' '} />
              </Stack>
              {form.id && (
                <TextField size="small" select label="Estado" value={form.status} onChange={e => setForm({ ...form, status: e.target.value as CustomerOrderStatus })}>
                  {(Object.keys(STATUS) as CustomerOrderStatus[]).map(s => <MenuItem key={s} value={s}>{STATUS[s].label}</MenuItem>)}
                </TextField>
              )}
              <TextField size="small" label="Notas" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} multiline placeholder="Ej. pactado por WhatsApp y correo" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          {form?.id && (
            <Button color="error" sx={{ mr: 'auto' }} onClick={async () => {
              if (!form.id || !window.confirm('¿Borrar la OC y sus entregas? Las ventas generadas no se borran.')) return;
              await api.deleteCustomerOrder(form.id);
              setForm(null);
              load();
            }}>Borrar</Button>
          )}
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.number || !form?.productId || !num(form?.quantity)}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {detailId && <OrderDetail id={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </>
  );
};

// ==================== Detalle y entregas ====================

const OrderDetail: React.FC<{ id: number; onClose: () => void }> = ({ id, onClose }) => {
  const [order, setOrder] = useState<CustomerOrder | null>(null);
  const [plants, setPlants] = useState<CustomerPlant[]>([]);
  const [history, setHistory] = useState<CustomerOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({ date: todayIso(), quantity: '', plantId: '' as number | '', plantName: '', remito: '', invoice: '', carrier: '', createSale: true });

  useEffect(() => {
    api.getCustomerOrder(id).then(o => {
      setOrder(o);
      api.getCustomerPlants(o.customerId).then(setPlants);
      api.getCustomerOrders({ customerId: o.customerId }).then(all => setHistory(all.filter(x => x.productId === o.productId)));
    }).catch(e => setError(errorMessage(e, 'No se pudo abrir la orden')));
  }, [id]);

  const addDelivery = async () => {
    if (!order) return;
    const quantity = num(d.quantity);
    setSaving(true);
    try {
      let orderId: number | null = null;
      if (d.createSale) {
        const sale = await api.createOrderForDelivery({
          customerId: order.customerId,
          reference: d.remito ? `REM-${d.remito}` : undefined,
          notes: `Entrega OC ${order.number}${d.plantName || d.plantId ? ` · ${plants.find(p => p.id === d.plantId)?.name || d.plantName}` : ''}`,
          items: [{ productId: order.productId, quantity, unitPrice: order.unitPrice }],
        });
        orderId = sale.id;
        // Entregada: descuenta el stock ahora y una sola vez (facturarla después no lo vuelve a tocar).
        await api.markOrderDelivered(sale.id);
      }
      const updated = await api.addCustomerOrderDelivery(order.id, {
        date: d.date, quantity, plantId: d.plantId || null, plantName: d.plantName || null,
        remitoNumber: d.remito || null, invoiceNumber: d.invoice || null, carrier: d.carrier || null, orderId, notes: null,
      });
      setOrder(updated);
      setD({ ...d, quantity: '', remito: '', invoice: '' });
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'No se pudo registrar la entrega'));
    } finally {
      setSaving(false);
    }
  };

  const removeDelivery = async (deliveryId: number, hasSale: boolean) => {
    const extra = hasSale ? ' La venta generada queda: anulala desde Ventas si corresponde.' : '';
    if (!order || !window.confirm(`¿Borrar esta entrega?${extra}`)) return;
    setOrder(await api.deleteCustomerOrderDelivery(order.id, deliveryId));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{order ? `${order.customerName} · OC ${order.number}` : 'Orden de compra'}</DialogTitle>
      <DialogContent>
        {!order ? (error ? <Alert severity="error">{error}</Alert> : <CircularProgress />) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2">{order.productName}</Typography>
              <Typography variant="body2">Pedido: <b>{fmtQty(order.quantity)}</b> a {fmtMoney2(order.unitPrice)}</Typography>
              <Typography variant="body2">Entregado: <b>{fmtQty(order.delivered)}</b> ({fmtPct(order.deliveredPct)})</Typography>
              <Typography variant="body2">Saldo: <b>{fmtQty(order.balance)}</b> ({fmtMoney(order.balance * order.unitPrice)})</Typography>
              <Chip size="small" color={STATUS[order.status].color} label={STATUS[order.status].label} />
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {order.status === 'Open' && (
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 1.5 }}>Registrar entrega</Typography>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField size="small" type="date" label="Fecha" value={d.date} onChange={e => setD({ ...d, date: e.target.value })} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" type="number" label="Cantidad" value={d.quantity} onChange={e => setD({ ...d, quantity: e.target.value })} helperText={`Saldo ${fmtQty(order.balance)}`} />
                    {plants.length > 0 ? (
                      <TextField size="small" select label="Planta" value={d.plantId} sx={{ minWidth: 200 }}
                        onChange={e => {
                          const p = plants.find(x => x.id === Number(e.target.value));
                          setD({ ...d, plantId: e.target.value === '' ? '' : Number(e.target.value), carrier: p?.carrier || d.carrier });
                        }}>
                        <MenuItem value="">—</MenuItem>
                        {plants.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </TextField>
                    ) : (
                      <TextField size="small" label="Planta / localidad" value={d.plantName} onChange={e => setD({ ...d, plantName: e.target.value })} />
                    )}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField size="small" label="N° de remito" value={d.remito} onChange={e => setD({ ...d, remito: e.target.value })} />
                    <TextField size="small" label="N° de factura" value={d.invoice} onChange={e => setD({ ...d, invoice: e.target.value })} />
                    <TextField size="small" label="Expreso" value={d.carrier} onChange={e => setD({ ...d, carrier: e.target.value })} sx={{ flex: 1 }} />
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <FormControlLabel sx={{ flex: 1 }}
                      control={<Checkbox checked={d.createSale} onChange={e => setD({ ...d, createSale: e.target.checked })} />}
                      label={`Generar la venta entregada en cuenta corriente (${fmtMoney(num(d.quantity) * order.unitPrice)} + IVA, descuenta stock)`} />
                    <Button variant="contained" startIcon={<LocalShipping />} onClick={addDelivery}
                      disabled={saving || num(d.quantity) <= 0 || num(d.quantity) > order.balance}>Registrar</Button>
                  </Stack>
                </Stack>
              </Card>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Planta</TableCell>
                  <TableCell>Remito</TableCell>
                  <TableCell>Factura</TableCell>
                  <TableCell>Expreso</TableCell>
                  <TableCell>Venta</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {order.deliveries.length === 0 && <TableRow><TableCell colSpan={8} sx={{ color: 'text.secondary' }}>Sin entregas todavía.</TableCell></TableRow>}
                {order.deliveries.map(x => (
                  <TableRow key={x.id}>
                    <TableCell>{formatDay(x.date)}</TableCell>
                    <TableCell align="right">{fmtQty(x.quantity)}</TableCell>
                    <TableCell>{x.plantName || ''}</TableCell>
                    <TableCell>{x.remitoNumber || ''}</TableCell>
                    <TableCell>{x.invoiceNumber || ''}</TableCell>
                    <TableCell>{x.carrier || ''}</TableCell>
                    <TableCell>{x.orderReference ? <Chip size="small" label={x.orderReference} /> : '—'}</TableCell>
                    <TableCell align="right"><IconButton size="small" onClick={() => removeDelivery(x.id, !!x.orderId)}><Delete fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {history.length > 1 && (
              <>
                <Divider />
                <Typography fontWeight={700}>Precios de {order.productName} a este cliente</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[...history].sort((a, b) => a.orderDate.localeCompare(b.orderDate)).map(h => (
                    <Chip key={h.id} variant={h.id === order.id ? 'filled' : 'outlined'} label={`${formatDay(h.orderDate)} · ${fmtMoney2(h.unitPrice)} · ${fmtQty(h.quantity)} u.`} />
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
};

// ==================== Plantas ====================

type PlantForm = Omit<CustomerPlant, 'id' | 'customerId' | 'customerName'> & { id?: number; customer: PickedCustomer | null };

const PlantsTab: React.FC = () => {
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [rows, setRows] = useState<CustomerPlant[]>([]);
  const [form, setForm] = useState<PlantForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => { api.getCustomerPlants(customer?.id).then(setRows); }, [customer]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form?.customer) { setError('Elegí el cliente'); return; }
    const { id, customer: c, ...rest } = form;
    const payload = { ...rest, customerId: c.id };
    try {
      if (id) await api.updateCustomerPlant(id, payload);
      else await api.createCustomerPlant(payload);
      setForm(null);
      setError(null);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar la planta'));
    }
  };

  const remove = async (p: CustomerPlant) => {
    if (!window.confirm(`¿Borrar la planta ${p.name}?`)) return;
    await api.deleteCustomerPlant(p.id);
    load();
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 260 }}><CustomerPicker value={customer} onChange={setCustomer} /></Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setForm({ customer, name: '', address: '', carrier: '', carrierAddress: '', carrierPhone: '', notes: '' })}>Nueva planta</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Planta</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell>Expreso</TableCell>
                <TableCell>Dirección expreso</TableCell>
                <TableCell>Tel. expreso</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin plantas cargadas.</TableCell></TableRow>}
              {rows.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.customerName}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.address || ''}</TableCell>
                  <TableCell>{p.carrier || ''}</TableCell>
                  <TableCell>{p.carrierAddress || ''}</TableCell>
                  <TableCell>{p.carrierPhone || ''}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => setForm({ ...p, customer: { id: p.customerId, name: p.customerName || '' } })}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => remove(p)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Editar planta' : 'Nueva planta'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <CustomerPicker value={form.customer} onChange={c => setForm({ ...form, customer: c })} />
              <TextField size="small" label="Planta / localidad" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Chivilcoy" />
              <TextField size="small" label="Dirección de la planta" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
              <TextField size="small" label="Expreso" value={form.carrier || ''} onChange={e => setForm({ ...form, carrier: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Dirección del expreso" value={form.carrierAddress || ''} onChange={e => setForm({ ...form, carrierAddress: e.target.value })} sx={{ flex: 1 }} />
                <TextField size="small" label="Teléfono" value={form.carrierPhone || ''} onChange={e => setForm({ ...form, carrierPhone: e.target.value })} />
              </Stack>
              <TextField size="small" label="Notas" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} multiline />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.name}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FactoryCustomerOrdersPage;
